'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowRight, ArrowLeft, CheckCircle, User, Users, Briefcase, HelpCircle, Phone, Mail, X } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


type FormData = {
  pourQui: string;
  corps: string;
  besoin: string;
  prenom: string;
  contactMode: string;
  email: string;
  telephone: string;
  disponibilites: string;
  message: string;
  consentement: boolean;
};

const TOTAL_STEPS = 4;

export default function OrientationForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      pourQui: '',
      corps: '',
      besoin: '',
      prenom: '',
      contactMode: 'email',
      email: '',
      telephone: '',
      disponibilites: '',
      message: '',
      consentement: false,
    },
  });

  const watchedValues = watch();

  const pourQuiOptions = [
    { id: 'pq-moi', value: 'pour-moi', label: 'Pour moi', icon: User },
    { id: 'pq-proche', value: 'pour-un-proche', label: 'Pour un proche', icon: Users },
    { id: 'pq-collegue', value: 'pour-un-collegue', label: 'Pour un collègue ou membre de mon équipe', icon: Briefcase },
    { id: 'pq-orga', value: 'organisation', label: 'Je représente une organisation', icon: HelpCircle },
  ];

  const corpsOptions = [
    { id: 'corps-sp', value: 'sapeurs-pompiers', label: 'Sapeurs-pompiers' },
    { id: 'corps-police', value: 'police', label: 'Police' },
    { id: 'corps-gendarmerie', value: 'gendarmerie', label: 'Gendarmerie' },
    { id: 'corps-autre', value: 'autre', label: 'Autre' },
    { id: 'corps-nr', value: 'ne-sais-pas', label: 'Je préfère ne pas répondre' },
  ];

  const besoinOptions = [
    { id: 'besoin-recontact', value: 'etre-recontacte', label: 'Être recontacté', description: 'Un référent APTE vous rappelle' },
    { id: 'besoin-comprendre', value: 'comprendre-accompagnement', label: 'Comprendre l\'accompagnement', description: 'En savoir plus sur la démarche' },
    { id: 'besoin-ressources', value: 'consulter-ressources', label: 'Consulter des ressources', description: 'Accéder à des contenus adaptés' },
    { id: 'besoin-rdv', value: 'demander-rdv', label: 'Demander un rendez-vous', description: 'Planifier un premier échange' },
    { id: 'besoin-sais-pas', value: 'ne-sais-pas', label: 'Je ne sais pas par où commencer', description: 'APTE vous guide' },
  ];

  const goNext = async () => {
    let fieldsToValidate: (keyof FormData)[] = [];
    if (currentStep === 1) fieldsToValidate = ['pourQui'];
    if (currentStep === 2) fieldsToValidate = ['corps'];
    if (currentStep === 3) fieldsToValidate = ['besoin'];

    const valid = fieldsToValidate.length === 0 || await trigger(fieldsToValidate);
    if (valid) setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 1));

  // Backend integration point: POST /api/orientation-requests
  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setIsSubmitting(false);
    setSubmitted(true);
    toast.success('Votre demande a bien été reçue.');
  };

  if (submitted) {
    return (
      <div className="card-apte p-8 text-center animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-3">Votre demande a bien été reçue</h2>
        <p className="text-muted-foreground mb-2">
          Un référent APTE prendra connaissance de votre demande et vous contactera selon vos préférences.
        </p>
        <p className="text-sm text-muted-foreground italic mb-8">
          Le délai de traitement sera précisé par l&apos;équipe APTE — aucune réponse immédiate n&apos;est garantie.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link href="/connexion-et-r-cup-ration-d-acc-s" className="btn-primary">
            Créer ou activer mon espace personnel
          </Link>
          <Link href="/ressources" className="btn-outline">
            Consulter des ressources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card-apte overflow-hidden">
      {/* Progress bar */}
      <div className="bg-secondary px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">Demande d&apos;orientation</span>
          <span className="text-sm text-muted-foreground">Étape {currentStep}/{TOTAL_STEPS}</span>
        </div>
        <div className="w-full bg-border rounded-full h-2" role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={TOTAL_STEPS}>
          <div
            className="bg-primary h-2 rounded-full transition-all duration-500"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
          />
        </div>
        <div className="flex gap-2 mt-3">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <span
              key={`step-dot-${i + 1}`}
              className={`text-xs font-medium px-2 py-0.5 rounded-full transition-smooth ${
                i + 1 === currentStep
                  ? 'bg-primary text-white'
                  : i + 1 < currentStep
                  ? 'bg-primary/20 text-primary' :'bg-border text-muted-foreground'
              }`}
            >
              {i + 1 < currentStep ? '✓' : i + 1}
            </span>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="p-6 lg:p-8">
          {/* Step 1 */}
          {currentStep === 1 && (
            <div className="animate-fade-in">
              <h2 className="text-lg font-bold text-foreground mb-2">Pour qui cherchez-vous un accompagnement ?</h2>
              <p className="text-sm text-muted-foreground mb-6">Cette information nous aide à adapter notre réponse.</p>
              <div className="space-y-3" role="radiogroup" aria-label="Pour qui">
                {pourQuiOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-smooth ${
                        watchedValues.pourQui === opt.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-secondary/50'
                      }`}
                    >
                      <input
                        type="radio"
                        value={opt.value}
                        {...register('pourQui', { required: 'Veuillez sélectionner une option' })}
                        className="sr-only"
                      />
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        watchedValues.pourQui === opt.value ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
                      }`}>
                        <Icon size={18} />
                      </div>
                      <span className="font-medium text-sm text-foreground">{opt.label}</span>
                      {watchedValues.pourQui === opt.value && (
                        <CheckCircle size={18} className="text-primary ml-auto" />
                      )}
                    </label>
                  );
                })}
              </div>
              {errors.pourQui && (
                <p className="text-danger text-sm mt-2" role="alert">{errors.pourQui.message}</p>
              )}
            </div>
          )}

          {/* Step 2 */}
          {currentStep === 2 && (
            <div className="animate-fade-in">
              <h2 className="text-lg font-bold text-foreground mb-2">Quel est votre environnement professionnel ?</h2>
              <p className="text-sm text-muted-foreground mb-6">Cette information nous aide à orienter vers les ressources adaptées. Vous pouvez choisir de ne pas répondre.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Environnement professionnel">
                {corpsOptions.map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-smooth ${
                      watchedValues.corps === opt.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-secondary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      value={opt.value}
                      {...register('corps', { required: 'Veuillez sélectionner une option' })}
                      className="sr-only"
                    />
                    <span className="font-medium text-sm text-foreground">{opt.label}</span>
                    {watchedValues.corps === opt.value && (
                      <CheckCircle size={16} className="text-primary ml-auto" />
                    )}
                  </label>
                ))}
              </div>
              {errors.corps && (
                <p className="text-danger text-sm mt-2" role="alert">{errors.corps.message}</p>
              )}
            </div>
          )}

          {/* Step 3 */}
          {currentStep === 3 && (
            <div className="animate-fade-in">
              <h2 className="text-lg font-bold text-foreground mb-2">Que souhaitez-vous faire aujourd&apos;hui ?</h2>
              <p className="text-sm text-muted-foreground mb-6">Vous pouvez changer d&apos;avis à tout moment.</p>
              <div className="space-y-3" role="radiogroup" aria-label="Besoin principal">
                {besoinOptions.map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-smooth ${
                      watchedValues.besoin === opt.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-secondary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      value={opt.value}
                      {...register('besoin', { required: 'Veuillez sélectionner une option' })}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm text-foreground">{opt.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{opt.description}</div>
                    </div>
                    {watchedValues.besoin === opt.value && (
                      <CheckCircle size={18} className="text-primary mt-0.5 flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>
              {errors.besoin && (
                <p className="text-danger text-sm mt-2" role="alert">{errors.besoin.message}</p>
              )}
            </div>
          )}

          {/* Step 4 */}
          {currentStep === 4 && (
            <div className="animate-fade-in space-y-5">
              <div>
                <h2 className="text-lg font-bold text-foreground mb-1">Vos coordonnées</h2>
                <p className="text-sm text-muted-foreground">Seules les informations nécessaires à vous recontacter sont demandées.</p>
              </div>

              {/* Prénom */}
              <div>
                <label htmlFor="prenom" className="block text-sm font-semibold text-foreground mb-1">
                  Prénom ou pseudonyme d&apos;usage <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">Utilisé uniquement pour personnaliser nos échanges.</p>
                <input
                  id="prenom"
                  type="text"
                  autoComplete="given-name"
                  className={`w-full px-4 py-3 rounded-lg border text-sm bg-white transition-smooth focus:outline-none focus:ring-2 focus:ring-ring ${
                    errors.prenom ? 'border-danger focus:ring-danger/30' : 'border-border focus:border-primary'
                  }`}
                  placeholder="Votre prénom ou un pseudonyme"
                  {...register('prenom', { required: 'Ce champ est obligatoire' })}
                />
                {errors.prenom && <p className="text-danger text-xs mt-1" role="alert">{errors.prenom.message}</p>}
              </div>

              {/* Moyen de contact */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Moyen de contact préféré <span className="text-danger">*</span>
                </label>
                <div className="flex gap-3">
                  {[
                    { id: 'contact-email', value: 'email', label: 'Email', icon: Mail },
                    { id: 'contact-phone', value: 'telephone', label: 'Téléphone', icon: Phone },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <label
                        key={opt.id}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer text-sm font-medium transition-smooth ${
                          watchedValues.contactMode === opt.value
                            ? 'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/40'
                        }`}
                      >
                        <input type="radio" value={opt.value} {...register('contactMode')} className="sr-only" />
                        <Icon size={15} />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Email */}
              {watchedValues.contactMode === 'email' && (
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1">
                    Adresse email <span className="text-danger">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`w-full px-4 py-3 rounded-lg border text-sm bg-white transition-smooth focus:outline-none focus:ring-2 focus:ring-ring ${
                      errors.email ? 'border-danger' : 'border-border focus:border-primary'
                    }`}
                    placeholder="votre@email.fr"
                    {...register('email', {
                      required: watchedValues.contactMode === 'email' ? 'Ce champ est obligatoire' : false,
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email invalide' },
                    })}
                  />
                  {errors.email && <p className="text-danger text-xs mt-1" role="alert">{errors.email.message}</p>}
                </div>
              )}

              {/* Téléphone */}
              {watchedValues.contactMode === 'telephone' && (
                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-foreground mb-1">
                    Numéro de téléphone <span className="text-danger">*</span>
                  </label>
                  <input
                    id="telephone"
                    type="tel"
                    autoComplete="tel"
                    className={`w-full px-4 py-3 rounded-lg border text-sm bg-white transition-smooth focus:outline-none focus:ring-2 focus:ring-ring ${
                      errors.telephone ? 'border-danger' : 'border-border focus:border-primary'
                    }`}
                    placeholder="06 00 00 00 00"
                    {...register('telephone', {
                      required: watchedValues.contactMode === 'telephone' ? 'Ce champ est obligatoire' : false,
                    })}
                  />
                  {errors.telephone && <p className="text-danger text-xs mt-1" role="alert">{errors.telephone.message}</p>}
                </div>
              )}

              {/* Disponibilités */}
              <div>
                <label htmlFor="disponibilites" className="block text-sm font-semibold text-foreground mb-1">
                  Disponibilités <span className="text-muted-foreground font-normal">(facultatif)</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2">Ex. : en semaine le matin, ou uniquement le soir.</p>
                <input
                  id="disponibilites"
                  type="text"
                  className="w-full px-4 py-3 rounded-lg border border-border text-sm bg-white transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary"
                  placeholder="Vos créneaux préférés"
                  {...register('disponibilites')}
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-1">
                  Message <span className="text-muted-foreground font-normal">(facultatif)</span>
                </label>
                <p className="text-xs text-muted-foreground mb-2 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                  ⚠️ Ne transmettez pas d&apos;informations médicales détaillées dans ce champ.
                </p>
                <textarea
                  id="message"
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-border text-sm bg-white transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary resize-none"
                  placeholder="Ce que vous souhaitez nous faire savoir..."
                  {...register('message')}
                />
              </div>

              {/* Consentement */}
              <div className={`p-4 rounded-xl border-2 transition-smooth ${errors.consentement ? 'border-danger bg-red-50' : 'border-border bg-secondary/30'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 w-5 h-5 rounded border-border accent-primary"
                    {...register('consentement', { required: 'Vous devez accepter pour continuer' })}
                  />
                  <span className="text-sm text-foreground leading-relaxed">
                    J&apos;accepte que mes coordonnées soient utilisées par APTE uniquement pour traiter ma demande d&apos;orientation associative. <span className="text-danger">*</span>
                  </span>
                </label>
                {errors.consentement && <p className="text-danger text-xs mt-2" role="alert">{errors.consentement.message}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 lg:px-8 pb-6 flex items-center justify-between gap-4 border-t border-border pt-5">
          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={goBack}
                className="btn-outline text-sm"
              >
                <ArrowLeft size={15} />
                Retour
              </button>
            )}
            <Link href="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-smooth px-2 py-2">
              <X size={14} />
              Quitter
            </Link>
          </div>

          {currentStep < TOTAL_STEPS ? (
            <button
              type="button"
              onClick={goNext}
              className="btn-primary text-sm"
              disabled={
                (currentStep === 1 && !watchedValues.pourQui) ||
                (currentStep === 2 && !watchedValues.corps) ||
                (currentStep === 3 && !watchedValues.besoin)
              }
            >
              Continuer
              <ArrowRight size={15} />
            </button>
          ) : (
            <button
              type="submit"
              className="btn-primary text-sm min-w-[160px] justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  Envoyer ma demande
                  <ArrowRight size={15} />
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}